import { useEffect, useContext, useState, useRef } from "react";
import {
  Skeleton,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";

import Table, {
  StyledTableCell,
  StyledTableRow,
} from "../../../Component/UI/Table/Table";
import MUICard from "../../../Component/UI/MUICard/MUICard";
import classes from "./CoreAttributes.module.css";
import { DiscoveryContext } from "../discovery-context";
import { extractDomain } from "../../../utils/helpers";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import api from "../../../BrandOnBoarding/api";
import MUIToolTip from "../../../Component/UI/MUIToolTip/MUIToolTip";
import DetailedTableModal from "../../BrandAudit/BrandAuditMetricDetails";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import { InfoOutlined } from "@mui/icons-material";
import { metricExplainations } from "../../../utils/constant";

const TABLE_HEADERS = [
  {
    id: "attr",
    name: "Brand Pillars",
  },
];

export default function CoreAttributes({ category, entity }) {
  const [error, setError] = useState();
  const discoveryCtx = useContext(DiscoveryContext);
  const abortControllerRef = useRef(null);
  const [showModal, setShowModal] = useState(false);


  function formatName(str) {
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  function renderRows(rowItem, index) {
    const attName = formatName(rowItem?.name);
    return (
      <StyledTableRow key={index}>
        <Tooltip title={metricExplainations?.[attName]}>
          <StyledTableCell className={classes.attribute_headings}>
            {formatName(rowItem?.name)}
          </StyledTableCell>
        </Tooltip>
        {rowItem.scores.map((item, index) => {
          let color = "#dc3545";
          let rating = "low";
          if (!item.score) {
            color = "#000";
          }
          if (item.score >= 7) {
            rating = "fair";
            color = "#2196F3";
          }
          if (item.score >= 9) {
            rating = "high";
            color = "#198754";
          }

          let sources = [];
          if (item?.sources) {
            sources = item.sources.map((source) => ({
              source: source,
              snippet: "",
            }));
          }

          return (
            item.score ? (
              <MUIToolTip
                key={index}
                title={item.explanation}
                additionalText={`${item.name} received a rating of ${item.score
                  } out of 10, a ${rating} score in ${formatName(rowItem.name)}`}
                arrow
                disableAction
                llmSources={sources}
                showSources
                llm={discoveryCtx.llm}
                actionText="Add To Actions"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, -18],
                        },
                      },
                    ],
                  },
                }}
              >
                <StyledTableCell
                  style={{ color }}
                  className={classes.attribute_score}
                >
                  {item.score}
                </StyledTableCell>
              </MUIToolTip>
            ) : (
              <StyledTableCell
                style={{ color }}
                className={classes.attribute_score}
              >
                -
              </StyledTableCell>
            )
          );
        })}
      </StyledTableRow>
    );
  }

  function LoadingSkeletion() {
    return (
      <div className="p-3">
        <Skeleton variant="rectangular" height={30} />
        {Array(8)
          .fill(0)
          .map((_, index) => (
            <Skeleton
              variant="rectangular"
              width={index % 2 === 0 ? "80%" : "97%"}
              key={index}
              className="my-3"
              height={20}
            />
          ))}
      </div>
    );
  }

  useEffect(() => {
    if (discoveryCtx.abort_launch) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
  }, [discoveryCtx.abort_launch]);

  useEffect(() => {
    if (
      discoveryCtx?.core_attributes &&
      Object.keys(discoveryCtx?.core_attributes).length > 0
    )
      return;
    // getCoreAttributes();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  function renderHeaders(coreAttributesData) {
    const headers = [
      ...TABLE_HEADERS,
      ...discoveryCtx.competitors.map((item) => ({
        id: item.name,
        name: item.name,
      })),
    ];

    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headers.map((header, index) => {
            let ctxCompetitor = null;
            if (index !== 0) {
              ctxCompetitor = discoveryCtx.competitors[index - 1];
            }

            let domain = "";
            if (ctxCompetitor && ctxCompetitor?.domain) {
              domain = extractDomain(ctxCompetitor?.domain);
            }

            return (
              <TableCell key={index} className={classes.table_head_cell}>
                <div className={classes.table_head_content}>
                  {index != 0 && (
                    <img
                      data-html2canvas-ignore
                      className={classes.table_head_logo}
                      src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
                    />
                  )}
                  <div
                    className={
                      index === 0
                        ? classes.table_head_text_comp
                        : classes.table_head_text
                    }
                  >
                    {/* {header.name} */}
                    <div className={classes.table_head_title}>
                      <span>
                        <Tooltip title={metricExplainations[header.name]}>
                          {header.name}
                        </Tooltip>
                      </span>
                      {index === 0 && !showModal && (
                        <MUIIconButton onClick={() => setShowModal(true)}>
                          <InfoOutlined />
                        </MUIIconButton>
                      )}
                    </div>
                  </div>
                </div>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  let coreAttributesData = [];
  let loading = true;

  if (
    discoveryCtx?.core_attributes &&
    Object.keys(discoveryCtx?.core_attributes).length > 0 &&
    discoveryCtx?.core_attributes?.summary
  ) {
    coreAttributesData = Object.keys(
      discoveryCtx.core_attributes.category_averages
    ).map((item) => {
      const scores = discoveryCtx.core_attributes.products.map((p) => ({
        name: p.name,
        score: p.attributes[item]?.score,
        explanation: p.attributes[item]?.explanation,
        sources: p.attributes[item]?.sources,
      }));
      return {
        name: item,
        scores,
      };
    });
    loading = false;
  }

  return (
    <MUICard variant="elevated" elevation={0} className={classes.card}>
      {coreAttributesData &&
        coreAttributesData.length > 0 &&
        !loading &&
        !error && (
          <>
            <Table
              elevation={0}
              data={coreAttributesData}
              renderHeaders={() => renderHeaders(coreAttributesData)}
              render={renderRows}
            />
            <DetailedTableModal
              show={showModal}
              onClose={() => setShowModal(false)}
              data={coreAttributesData}
              headers={discoveryCtx.competitors.map((item) => item.name)}
              renderHeaders={renderHeaders}
              formatName={formatName}
              classes={classes}
              pdfHeight={400}
              pdfTitle={`${entity}-Brand Pillars`}
            />
          </>
        )}
      {loading && !error && <LoadingSkeletion />}
      {error && (
        <div className={classes.error}>
          <ErrorView
            title={"Error fetching core attributes"}
            message={error}
            retry
          // onRetry={() => getCoreAttributes()}
          />
        </div>
      )}
    </MUICard>
  );
}
